import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import cn from 'classnames';
import { BottomSheetContentLayout, MenuItem, PopoverOnHover, Row, Typography, useOverlayContext } from 'seagull';
import { Logout } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { routes } from '~/constants';
import { useStore, useStoreActions } from '~/contexts';
import { useEventReporter } from '~/event-reporter/EventReporter';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useIsMobileForHomePage } from '~/hooks/useIsMobileForHomePage';
import { useNativeFilteredNavLinks } from '~/hooks/useNativeFilteredNavLinks';
import { useLocaleKeys } from '~/i18n';
import { UserRole } from '~/models';
import { selectors } from '~/store/state-selectors';
import { InitialsAvatar } from './InitialsAvatar';
import { LanguageSwitcher } from './LanguageSwitcher';
import styles from './UserMenu.module.scss';
const Separator = ()=>/*#__PURE__*/ _jsx("div", {
        className: styles.separator
    });
export const GetUserMenuTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            UserMenuTestIds: {
                SettingsButton: 'home-sidebar-settings-btn',
                Avatar: 'home-sidebar-avatar',
                OverflowMenu: 'home-sidebar-avatar-overflow-menu',
                Logout: 'home-sidebar-logout',
                MenuItem: (id)=>"home-sidebar-".concat(id)
            }
        }
    });
export const userMenuRouteTextsPath = 'components.userMenu';
export const getUserMenuRoutes = (texts)=>[
        {
            id: routes.profile,
            text: texts.profile(),
            url: routes.profile,
            shouldShow: true
        },
        {
            id: routes.anchorBilling,
            text: texts.anchorBilling(),
            url: routes.anchorBilling,
            shouldShow: (param)=>{
                let { isVendor, role } = param;
                return isVendor && role !== UserRole.Member;
            }
        },
        {
            id: routes.importAgreements,
            text: texts.importAgreements(),
            url: routes.importAgreements,
            shouldShow: (param)=>{
                let { isVendor } = param;
                return isVendor;
            },
            hideOnMobile: true
        }
    ];
export default function UserMenu(props) {
    const { userInfo, testId } = props;
    const { UserMenuTestIds } = useMemo(()=>GetUserMenuTID(testId), [
        testId
    ]);
    const isMobileDevice = useIsMobileForHomePage();
    const { showBottomSheet, hideBottomSheet } = useOverlayContext();
    const userInitials = userInfo.userInitials();
    const history = useHistory();
    const { reset } = useStoreActions();
    const { sendEventToGtm } = useEventReporter();
    const { t: userMenuTexts } = useLocaleKeys(userMenuRouteTextsPath);
    const { LANGUAGES } = useFeatureFlags();
    const filteredUserMenuRoutes = useNativeFilteredNavLinks(getUserMenuRoutes(userMenuTexts).filter((route)=>isMobileDevice ? !route.hideOnMobile : true));
    const topbarLinks = useStore(selectors.filterAvailableRoutes(filteredUserMenuRoutes));
    function renderTrigger(triggerProps) {
        return /*#__PURE__*/ _jsx("button", _object_spread_props(_object_spread({
            "data-testid": UserMenuTestIds.SettingsButton()
        }, triggerProps), {
            children: /*#__PURE__*/ _jsx(InitialsAvatar, {
                userInitials: userInitials,
                "data-testid": UserMenuTestIds.Avatar(),
                isActive: triggerProps['data-popover-is-open'] === 'true'
            })
        }));
    }
    function renderContent(param) {
        let { onCloseMenu } = param;
        const userName = userInfo.fullName();
        const userEmail = userInfo.email();
        const wrapper = (children)=>isMobileDevice ? /*#__PURE__*/ _jsx(BottomSheetContentLayout, {
                header: {
                    title: userName,
                    subtitle: userEmail
                },
                children: children
            }) : /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: styles.avatarContainer,
                        children: [
                            /*#__PURE__*/ _jsx(InitialsAvatar, {
                                userInitials: userInitials
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                children: [
                                    /*#__PURE__*/ _jsx(Typography, {
                                        weight: "normal",
                                        children: userName
                                    }),
                                    /*#__PURE__*/ _jsx(Typography, {
                                        variant: "paragraph3",
                                        skin: "secondary",
                                        children: userEmail
                                    })
                                ]
                            })
                        ]
                    }),
                    children
                ]
            });
        return /*#__PURE__*/ _jsx("div", {
            className: cn(styles.root, {
                [styles.mobile]: isMobileDevice
            }),
            "data-testid": UserMenuTestIds.OverflowMenu(),
            children: wrapper(/*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(Separator, {}),
                    topbarLinks.map((param)=>{
                        let { url, id, text } = param;
                        return /*#__PURE__*/ _jsx(MenuItem, {
                            skin: "secondary",
                            testId: UserMenuTestIds.MenuItem(id),
                            onClick: ()=>{
                                history.push(url);
                                onCloseMenu();
                            },
                            children: text
                        }, id);
                    }),
                    LANGUAGES && /*#__PURE__*/ _jsx(LanguageSwitcher, {
                        onCloseMenu: onCloseMenu
                    }),
                    /*#__PURE__*/ _jsx(Separator, {}),
                    /*#__PURE__*/ _jsx(MenuItem, {
                        skin: "secondary",
                        testId: UserMenuTestIds.Logout(),
                        onClick: ()=>{
                            sendEventToGtm('logout', {});
                            reset(true);
                            history.push(routes.home);
                            onCloseMenu();
                        },
                        children: /*#__PURE__*/ _jsxs(Row, {
                            gap: 12,
                            alignItems: "center",
                            children: [
                                /*#__PURE__*/ _jsx(Logout, {}),
                                userMenuTexts.logOut()
                            ]
                        })
                    })
                ]
            }))
        });
    }
    return isMobileDevice ? renderTrigger({
        onClick: ()=>showBottomSheet({
                content: renderContent({
                    onCloseMenu: ()=>hideBottomSheet()
                })
            })
    }) : /*#__PURE__*/ _jsx(PopoverOnHover, {
        placement: "bottom-end",
        openStrategy: "click",
        removePadding: true,
        showArrow: true,
        trigger: renderTrigger,
        content: (param)=>{
            let { closePopover } = param;
            return renderContent({
                onCloseMenu: ()=>closePopover()
            });
        }
    });
}
